import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Divider,
  FormHelperText,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { db } from '../../../firebase/config';
import { collection, doc, addDoc, updateDoc, deleteDoc, getDocs, Timestamp } from 'firebase/firestore';

type DataSource = {
  id: string;
  name: string;
  type: string;
  apiKey?: string;
  apiEndpoint?: string;
  apiVersion?: string;
  status: 'active' | 'inactive' | 'error';
  lastSync?: Timestamp;
  eventCount?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  error?: string;
  configuration?: Record<string, any>;
  categoryMappings?: Record<string, string>;
};

const sourceTypes = [
  { value: 'eventbrite', label: 'Eventbrite' },
  { value: 'meetup', label: 'Meetup' },
  { value: 'facebook', label: 'Facebook Events' },
  { value: 'manual', label: 'Manual Source' },
  { value: 'custom', label: 'Custom API' }
];

const DataSourcesConfig: React.FC = () => {
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [testingSourceId, setTestingSourceId] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Partial<DataSource>>({
    name: '',
    type: '',
    apiKey: '',
    apiEndpoint: '',
    apiVersion: '',
    status: 'inactive',
    configuration: {}
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Fetch data sources
  useEffect(() => {
    fetchDataSources();
  }, []);

  const fetchDataSources = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // In a real implementation, fetch from Firestore
      // For now, using mock data
      const mockDataSources: DataSource[] = [
        {
          id: '1',
          name: 'Eventbrite Official',
          type: 'eventbrite',
          apiKey: '***********',
          apiEndpoint: 'https://www.eventbriteapi.com/v3/',
          apiVersion: 'v3',
          status: 'active',
          lastSync: Timestamp.fromDate(new Date(Date.now() - 3600000)),
          eventCount: 845,
          createdAt: Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 3600000)),
          updatedAt: Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 3600000)),
          configuration: {
            categories: ['music', 'food-and-drink', 'charity-and-causes'],
            locationRadius: 50,
            locationUnit: 'mi',
            includeOnline: true
          }
        },
        {
          id: '2',
          name: 'Meetup Groups',
          type: 'meetup',
          apiKey: '***********',
          apiEndpoint: 'https://api.meetup.com/gql',
          status: 'active',
          lastSync: Timestamp.fromDate(new Date(Date.now() - 12 * 3600000)),
          eventCount: 632,
          createdAt: Timestamp.fromDate(new Date(Date.now() - 45 * 24 * 3600000)),
          updatedAt: Timestamp.fromDate(new Date(Date.now() - 2 * 24 * 3600000)),
          configuration: {
            categories: ['tech', 'career', 'outdoors'],
            includeOnline: true
          }
        },
        {
          id: '3',
          name: 'Facebook Events',
          type: 'facebook',
          apiKey: '***********',
          apiEndpoint: 'https://graph.facebook.com/v18.0/',
          apiVersion: 'v18.0',
          status: 'error',
          lastSync: Timestamp.fromDate(new Date(Date.now() - 48 * 3600000)),
          eventCount: 450,
          createdAt: Timestamp.fromDate(new Date(Date.now() - 60 * 24 * 3600000)),
          updatedAt: Timestamp.fromDate(new Date(Date.now() - 1 * 24 * 3600000)),
          error: 'API rate limit exceeded',
          configuration: {
            pages: ['page1', 'page2', 'page3'],
            includePrivate: false
          }
        }
      ];
      
      setDataSources(mockDataSources);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data sources:', err);
      setError('Failed to load data sources. Please try again.');
      setLoading(false);
    }
  };

  const handleOpenDialog = (sourceId?: string) => {
    if (sourceId) {
      const source = dataSources.find(s => s.id === sourceId);
      if (source) {
        setFormValues({
          name: source.name,
          type: source.type,
          apiKey: source.apiKey || '',
          apiEndpoint: source.apiEndpoint || '',
          apiVersion: source.apiVersion || '',
          status: source.status,
          configuration: source.configuration || {}
        });
        setEditingSourceId(sourceId);
      }
    } else {
      setFormValues({
        name: '',
        type: '',
        apiKey: '',
        apiEndpoint: '',
        apiVersion: '',
        status: 'inactive',
        configuration: {}
      });
      setEditingSourceId(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setValidationErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formValues.name?.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formValues.type) {
      errors.type = 'Source type is required';
    }
    
    if (formValues.type && formValues.type !== 'manual') {
      if (!formValues.apiKey?.trim()) {
        errors.apiKey = 'API Key is required';
      }
      
      if (!formValues.apiEndpoint?.trim()) {
        errors.apiEndpoint = 'API Endpoint is required';
      } else if (!formValues.apiEndpoint.startsWith('http')) {
        errors.apiEndpoint = 'API Endpoint must be a valid URL';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      if (editingSourceId) {
        // Update existing source
        const updatedSource: DataSource = {
          ...dataSources.find(s => s.id === editingSourceId)!,
          ...formValues,
          updatedAt: Timestamp.fromDate(new Date())
        } as DataSource;
        
        // In a real implementation, you would update Firestore
        // await updateDoc(doc(db, 'eventSources', editingSourceId), updatedSource);
        
        // Update local state
        setDataSources(prev => 
          prev.map(source => source.id === editingSourceId ? updatedSource : source)
        );
      } else {
        // Create new source
        const newSource: DataSource = {
          ...formValues,
          id: Math.random().toString(36).substring(2, 9), // Temporary ID
          status: 'inactive',
          createdAt: Timestamp.fromDate(new Date()),
          updatedAt: Timestamp.fromDate(new Date()),
        } as DataSource;
        
        // In a real implementation, you would add to Firestore
        // const docRef = await addDoc(collection(db, 'eventSources'), newSource);
        // newSource.id = docRef.id;
        
        // Update local state
        setDataSources(prev => [...prev, newSource]);
      }
      
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving data source:', err);
      setError('Failed to save data source. Please try again.');
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    if (window.confirm('Are you sure you want to delete this data source? All associated events will remain in the database.')) {
      try {
        // In a real implementation, you would delete from Firestore
        // await deleteDoc(doc(db, 'eventSources', sourceId));
        
        // Update local state
        setDataSources(prev => prev.filter(source => source.id !== sourceId));
      } catch (err) {
        console.error('Error deleting data source:', err);
        setError('Failed to delete data source. Please try again.');
      }
    }
  };

  const handleTestConnection = async (sourceId: string) => {
    try {
      setTestingSourceId(sourceId);
      
      // Simulate API connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update source status (simulated success)
      setDataSources(prev => 
        prev.map(source => {
          if (source.id === sourceId) {
            return {
              ...source,
              status: 'active',
              error: undefined,
              updatedAt: Timestamp.fromDate(new Date())
            };
          }
          return source;
        })
      );
      
      alert('Connection successful!');
    } catch (err) {
      console.error('Error testing connection:', err);
      setError('Failed to test connection. Please check your API credentials.');
    } finally {
      setTestingSourceId(null);
    }
  };

  const handleTriggerSync = async (sourceId: string) => {
    try {
      // This would trigger a Cloud Function to sync data
      alert('Sync job triggered! Check the Scheduled Jobs tab to monitor progress.');
    } catch (err) {
      console.error('Error triggering sync:', err);
      setError('Failed to trigger sync job. Please try again.');
    }
  };

  const renderSourceTypeFields = () => {
    const sourceType = formValues.type;
    
    if (!sourceType || sourceType === 'manual') {
      return null;
    }
    
    return (
      <>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            name="apiKey"
            label="API Key"
            value={formValues.apiKey || ''}
            onChange={handleInputChange}
            error={!!validationErrors.apiKey}
            helperText={validationErrors.apiKey}
            type="password"
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            required
            fullWidth
            name="apiEndpoint"
            label="API Endpoint"
            value={formValues.apiEndpoint || ''}
            onChange={handleInputChange}
            error={!!validationErrors.apiEndpoint}
            helperText={validationErrors.apiEndpoint || 'Example: https://api.example.com/'}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            name="apiVersion"
            label="API Version"
            value={formValues.apiVersion || ''}
            onChange={handleInputChange}
            helperText="Optional"
          />
        </Grid>
        {sourceType === 'eventbrite' && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Categories</InputLabel>
              <Select
                multiple
                value={formValues.configuration?.categories || []}
                onChange={(e) => {
                  const categories = e.target.value as string[];
                  setFormValues(prev => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      categories
                    }
                  }));
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value="music">Music</MenuItem>
                <MenuItem value="food-and-drink">Food & Drink</MenuItem>
                <MenuItem value="charity-and-causes">Charity & Causes</MenuItem>
                <MenuItem value="community">Community</MenuItem>
                <MenuItem value="arts">Arts</MenuItem>
                <MenuItem value="sports-and-fitness">Sports & Fitness</MenuItem>
              </Select>
              <FormHelperText>Select event categories to include</FormHelperText>
            </FormControl>
          </Grid>
        )}
        {(sourceType === 'eventbrite' || sourceType === 'meetup') && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.configuration?.includeOnline || false}
                  onChange={(e) => {
                    setFormValues(prev => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        includeOnline: e.target.checked
                      }
                    }));
                  }}
                />
              }
              label="Include online events"
            />
          </Grid>
        )}
      </>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon />;
      case 'inactive':
        return <WarningIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <WarningIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Event Data Sources
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Data Source
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {dataSources.map((source) => (
          <Grid item xs={12} md={6} lg={4} key={source.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" component="div">
                    {source.name}
                  </Typography>
                  <Chip 
                    label={source.status} 
                    color={getStatusColor(source.status)}
                    size="small"
                    icon={getStatusIcon(source.status)}
                  />
                </Box>
                <Typography color="text.secondary" gutterBottom>
                  {sourceTypes.find(t => t.value === source.type)?.label || source.type}
                </Typography>
                
                {source.error && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    {source.error}
                  </Alert>
                )}
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" component="div">
                    <strong>Last Sync:</strong> {source.lastSync ? new Date(source.lastSync.seconds * 1000).toLocaleString() : 'Never'}
                  </Typography>
                  {source.eventCount !== undefined && (
                    <Typography variant="body2" component="div">
                      <strong>Events:</strong> {source.eventCount}
                    </Typography>
                  )}
                  <Typography variant="body2" component="div">
                    <strong>Endpoint:</strong> {source.apiEndpoint ? source.apiEndpoint.substring(0, 30) + (source.apiEndpoint.length > 30 ? '...' : '') : 'N/A'}
                  </Typography>
                </Box>
              </CardContent>
              <Divider />
              <CardActions>
                <Tooltip title="Edit Data Source">
                  <IconButton onClick={() => handleOpenDialog(source.id)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Test Connection">
                  <IconButton 
                    onClick={() => handleTestConnection(source.id)} 
                    disabled={testingSourceId === source.id}
                    color="primary"
                  >
                    {testingSourceId === source.id ? (
                      <CircularProgress size={24} />
                    ) : (
                      <SettingsIcon />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Trigger Manual Sync">
                  <IconButton 
                    onClick={() => handleTriggerSync(source.id)}
                    disabled={source.status !== 'active'}
                    color="secondary"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Delete Data Source">
                  <IconButton 
                    onClick={() => handleDeleteSource(source.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}

        {dataSources.length === 0 && !loading && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                No data sources configured yet.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Your First Data Source
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Source Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSourceId ? 'Edit Data Source' : 'Add New Data Source'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="name"
                label="Source Name"
                value={formValues.name || ''}
                onChange={handleInputChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!validationErrors.type}>
                <InputLabel>Source Type</InputLabel>
                <Select
                  name="type"
                  value={formValues.type || ''}
                  onChange={handleSelectChange}
                >
                  {sourceTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.type && (
                  <FormHelperText>{validationErrors.type}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {renderSourceTypeFields()}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
          >
            {editingSourceId ? 'Update' : 'Add'} Data Source
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataSourcesConfig;
